import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { useAskRedoEnabled } from "@redotech/redo-merchant-app-common/ask-redo/use-ask-redo-enabled";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { TeamAnonymizationSource } from "@redotech/redo-model/team/anonymization";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { isBeforeNow } from "@redotech/util/date";
import { useContext } from "react";
import { ActionRequiredSeverity } from "./action-required-notification";

export function getTeamDeletionMessage(
  anonymizationDate: string | Date,
  source?: TeamAnonymizationSource | null,
) {
  const deletionDate = new Date(anonymizationDate).toLocaleDateString();
  if (source === TeamAnonymizationSource.ShopifyShopRedact) {
    return `This merchant account is scheduled for deletion on ${deletionDate} because the Shopify app was uninstalled. If you believe this is in error, please let us know.`;
  }

  return `This merchant account is scheduled for deletion on ${deletionDate} due to inactivity. If you believe this is in error, please let us know.`;
}

export function useTeamDeletion(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const team = useContext(TeamContext);

  // Open the appropriate AI panel depending on the rollout flag —
  // we don't want this banner to silently keep opening the legacy
  // panel once teams flip to the new Ask Redo UI.
  const { open: openLegacy } = useAiSlidePanel();
  const { open: openAskRedo } = useAskRedoSlidePanel();
  const { enabled: askRedoEnabled } = useAskRedoEnabled();
  const openContactSupport = askRedoEnabled
    ? () => openAskRedo()
    : () => openLegacy();

  const anonymizationDate = team?.teamAnonymization?.anonymizationDate;

  const hasAnonymizationDatePassed =
    !!anonymizationDate && isBeforeNow(new Date(anonymizationDate));

  if (!anonymizationDate || hasAnonymizationDatePassed) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.Critical,
    item: {
      id: "team-deletion",
      type: "error",
      message: getTeamDeletionMessage(
        anonymizationDate,
        team?.teamAnonymization?.source,
      ),
      actions: [
        {
          actionType: "button",
          text: "Contact support",
          hierarchy: "primary",
          onClick: openContactSupport,
        },
      ],
    },
  };
}
